import { useSyncExternalStore } from 'react'

// Real dark-mode support, module-level state rather than a Context
// provider (see auth.tsx's AuthProvider for this codebase's provider
// convention -- a Context wasn't needed here since nothing about the
// theme is per-request/per-user the way auth is, and AppShell's mobile
// drawer + desktop sidebar render two ThemeToggle instances
// simultaneously that must stay in sync, which useSyncExternalStore's
// shared module-level store gives for free without any <Provider> in
// the tree). index.html has its own tiny inline copy of the
// read-initial-theme logic so the `dark` class is set before first
// paint (React hydration runs after) -- keep the two in sync if this
// ever changes.
export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'remindloop_theme'

function prefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light'
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored
  } catch {
    // localStorage unavailable (private browsing, disabled storage, etc.)
    // -- fall through to the media-query default.
  }
  return prefersDark() ? 'dark' : 'light'
}

function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return
  document.documentElement.classList.toggle('dark', theme === 'dark')
}

let currentTheme: Theme = readInitialTheme()
applyTheme(currentTheme)

const listeners = new Set<() => void>()

function setTheme(theme: Theme) {
  currentTheme = theme
  applyTheme(theme)
  try {
    window.localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // persistence is best-effort -- the theme still applies for this
    // page view even if it can't be remembered for the next one.
  }
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot(): Theme {
  return currentTheme
}

// Prerendering (scripts/prerender.mjs -> entry-server.tsx) runs
// renderToString in plain Node, no window/document -- give it a fixed
// snapshot rather than touching browser globals.
function getServerSnapshot(): Theme {
  return 'light'
}

export function useTheme(): { theme: Theme; toggleTheme: () => void; setTheme: (theme: Theme) => void } {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  function toggleTheme() {
    setTheme(currentTheme === 'dark' ? 'light' : 'dark')
  }
  return { theme, toggleTheme, setTheme }
}
